import {
  conditionGroupHolds,
  hiddenFieldIds,
  rowOptions,
  vocabularyOf,
} from '@inference.engine/taxonomy/web';
import { getRuleBranches } from '@/entities/taxonomy';
import { get } from './lookup';
import type { TaxonomyField, TaxonomyFieldValue, TaxonomyRule } from '@/entities/taxonomy';
import type { TaxonomyRecordCells } from '@/entities/taxonomy-dataset';
import type { ResolvedList } from '@/entities/taxonomy-list';

/**
 * What a schema's rules say about one record, once its current values are known.
 *
 * Rules have been storable since the schema builder shipped and enforced
 * nowhere: `taxonomy-rule.types.ts` says outright that "enforcement at
 * name-generation time is a later concern". This is that concern. Without it a
 * grid happily offers values the schema's own governance forbids, which is the
 * failure the whole module exists to prevent.
 *
 * Evaluated PER RECORD, because every action depends on that record's own
 * values — the same rule hides a column in one row and not the next.
 */
export interface EffectiveField {
  /** Options this cell may offer, already narrowed by `limit_options`. */
  options: TaxonomyFieldValue[];
  /** `show_field` hid it for this record. */
  hidden: boolean;
  /** `require_field`, or the field's own `required`. */
  required: boolean;
  /** `set_default`, applied only to a cell with no value yet. */
  suggestedDefault?: string;
}

export type EffectiveFields = Record<string, EffectiveField>;

/** The vocabulary a field draws from, with the grid's resolved lists. */
export function vocabularySource(
  field: TaxonomyField,
  resolvedLists: Record<string, ResolvedList>,
): readonly TaxonomyFieldValue[] {
  return vocabularyOf(field, get(resolvedLists, field.id)?.values);
}

export { usableOptions } from '@inference.engine/taxonomy/web';

/**
 * Applies every rule branch that fires for this record.
 *
 * Order-independent by construction: `limit_options` intersects, `require_field`
 * and `set_default` are idempotent, and `show_field` is resolved from the whole
 * set of branches targeting a field rather than the last one seen.
 */
export function applyRules(
  fields: readonly TaxonomyField[],
  rules: readonly TaxonomyRule[],
  cells: TaxonomyRecordCells,
  resolvedLists: Record<string, ResolvedList>,
): EffectiveFields {
  const effective: EffectiveFields = {};
  for (const field of fields) {
    effective[field.id] = {
      options: rowOptions(vocabularySource(field, resolvedLists), field.id, rules, cells),
      hidden: false,
      required: field.required,
    };
  }

  for (const fieldId of hiddenFieldIds(fields, rules, cells)) {
    const target = get(effective, fieldId);
    if (target) {
      target.hidden = true;
    }
  }

  for (const rule of rules) {
    for (const branch of getRuleBranches(rule)) {
      const target = get(effective, branch.targetFieldId);
      // A target that no longer exists is reported by Validate as `rule_dead_field`. Options and
      // visibility were settled above, from the shared rules.
      if (!target || !conditionGroupHolds(branch.when, cells)) {
        continue;
      }

      switch (branch.action) {
        case 'set_default':
          if (branch.defaultValue) {
            target.suggestedDefault = branch.defaultValue;
          }
          break;
        case 'require_field':
          target.required = true;
          break;
      }
    }
  }

  return effective;
}
